import { reportAppearanceText } from '../utils/reportAppearanceText';
import AyanamshaSettings from '../components/AyanamshaSettings';
import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  Checkbox,
  Container,
  IconButton,
  Paper,
  Select,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { DarkModeOutlined, FileDownloadOutlined, LightModeOutlined, NightsStayOutlined } from '@mui/icons-material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedFormControlLabel as FormControlLabel, LocalizedMenuItem as MenuItem } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import CitySearch from '../components/forms/CitySearch';
import { AppMode, AYANAMSHA_OPTIONS, AyanamshaMode, DEFAULT_CHART_SECTION_OPTIONS, DefaultChartSection, DefaultCity, HOUSE_SYSTEM_OPTIONS, HouseSystemMode, KundliChartStyle, NodeMode, THEME_PALETTE_OPTIONS, ThemePaletteName, useUserPreferences } from '../contexts/UserPreferencesContext';
import { LANGUAGE_OPTIONS, SupportedLanguage, getLanguagePreferenceName, normalizeLanguage } from '../i18n/languages';
import { translateInline } from '../i18n/inlineTranslations';
import { DateDisplayFormat } from '../utils/dateDisplay';
import { studioApi } from '../api/studioClient';

const Settings: React.FC = () => {
  const [exportingCharts, setExportingCharts] = useState(false);
  const [exportError, setExportError] = useState('');
  const {
    chartStyle,
    setChartStyle,
    defaultChartSection,
    setDefaultChartSection,
    mode,
    setMode,
    dateDisplayFormat,
    setDateDisplayFormat,
    themePalette,
    setThemePalette,
    defaultCity,
    setDefaultCity,
    nodeMode,
    setNodeMode,
    houseSystem,
    setHouseSystem,
    ayanamshaMode,
    setAyanamshaMode,
    useKpForNadiKp,
    setUseKpForNadiKp,
    showOuterPlanets,
    setShowOuterPlanets,
    saveHoraryCharts,
    setSaveHoraryCharts,
    pdfTajikDefaultYear,
    setPdfTajikDefaultYear,
    pdfColorSameAsApp, pdfThemePalette, setPdfReportColor,
    pdfLanguageSameAsApp,
    setPdfLanguageSameAsApp,
    pdfLanguage,
    setPdfLanguage
  } = useUserPreferences();
  const { i18n } = useTranslation();
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const isHindi = language === 'hi';
  const text = (english: string, hindi?: string) => reportAppearanceText(language, english) || translateInline(language, english, hindi);

  const exportCharts = async () => {
    if (exportingCharts) return;
    setExportingCharts(true); setExportError('');
    try {
      const response = await studioApi.get('/charts/export/txt', { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url; link.download = 'kundlistar-studio-charts.txt'; document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error: any) { setExportError(error.message || 'Could not export saved charts.'); }
    finally { setExportingCharts(false); }
  };

  const changeLanguage = (nextLanguage: SupportedLanguage) => {
    i18n.changeLanguage(nextLanguage);
    if (localStorage.getItem('nadiAstroToken')) {
      axios.put('/users/profile', {
        preferences: { language: getLanguagePreferenceName(nextLanguage) },
      }).catch(() => {});
    }
  };

  const changeChartStyle = (_event: React.MouseEvent<HTMLElement>, nextStyle: KundliChartStyle | null) => {
    if (nextStyle) setChartStyle(nextStyle);
  };

  const changeMode = (_event: React.MouseEvent<HTMLElement>, nextMode: AppMode | null) => {
    if (nextMode) setMode(nextMode);
  };

  const changeNodeMode = (_event: React.MouseEvent<HTMLElement>, nextNodeMode: NodeMode | null) => {
    if (nextNodeMode) setNodeMode(nextNodeMode);
  };

  const resetDefaults = () => {
    setChartStyle('north');
    setMode('light');
    setDateDisplayFormat('indian');
    setThemePalette('green');
    setDefaultCity(null);
    setNodeMode('true');
    setHouseSystem('KP_PLACIDUS');
    setAyanamshaMode('LAHIRI');
    setUseKpForNadiKp(true);
    setShowOuterPlanets(false);
    setSaveHoraryCharts(false);
    setPdfTajikDefaultYear(new Date().getFullYear());
    setPdfLanguageSameAsApp(true);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Typography variant="h1" sx={{ fontSize: 20, mb: 0.5 }}>
          Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          App preferences and calculation defaults for Kundlistar Studio.
        </Typography>

        <SettingSection title="App preferences">
          <SettingRow label="Language">
            <Select fullWidth size="small" value={language} onChange={(event) => changeLanguage(event.target.value as SupportedLanguage)}>
              {LANGUAGE_OPTIONS.map((option) => <MenuItem key={option.code} value={option.code}>{option.label}</MenuItem>)}
            </Select>
          </SettingRow>

          <SettingRow label={text('PDF Language')}>
            <FormControlLabel
              control={<Checkbox checked={pdfLanguageSameAsApp} onChange={(event) => setPdfLanguageSameAsApp(event.target.checked)} />}
              label={text('PDF language same as app language')}
            />
            <Select
              fullWidth
              size="small"
              disabled={pdfLanguageSameAsApp}
              value={pdfLanguageSameAsApp ? language : (pdfLanguage || language)}
              onChange={(event) => setPdfLanguage(event.target.value as SupportedLanguage)}
            >
              {LANGUAGE_OPTIONS.map((option) => <MenuItem key={option.code} value={option.code}>{option.label}</MenuItem>)}
            </Select>
          </SettingRow>

          <SettingRow label={text('Date display format')}>
            <ToggleButtonGroup
              exclusive
              value={dateDisplayFormat}
              onChange={(_event, value: DateDisplayFormat | null) => value && setDateDisplayFormat(value)}
              fullWidth
            >
              <ToggleButton value="indian">DD/MM/YYYY</ToggleButton>
              <ToggleButton value="american">MM DD, YYYY</ToggleButton>
            </ToggleButtonGroup>
            <Alert severity="info" sx={{ mt: 1 }}>
              {text('This changes displayed dates only. Date entry remains unchanged.')}
            </Alert>
          </SettingRow>

          <SettingRow label={text('Mode')}>
            <ToggleButtonGroup exclusive value={mode} onChange={changeMode} fullWidth aria-label={text('Mode')}>
            <ToggleButton value="light"><LightModeOutlined fontSize="small" sx={{ mr: 0.75 }} />{text('Light')}</ToggleButton>
            <ToggleButton value="dark"><DarkModeOutlined fontSize="small" sx={{ mr: 0.75 }} />{text('Dark')}</ToggleButton>
            <ToggleButton value="night"><NightsStayOutlined fontSize="small" sx={{ mr: 0.75 }} />{text('Night')}</ToggleButton>
          </ToggleButtonGroup>
          </SettingRow>

          <SettingRow label={text('App colour')}>
            <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
              {THEME_PALETTE_OPTIONS.map((option) => {
                const active = option.value === themePalette;
                return (
                  <Button
                    key={option.value}
                    variant={active ? 'contained' : 'outlined'}
                    onClick={() => setThemePalette(option.value as ThemePaletteName)}
                    sx={{
                      minWidth: 106,
                      justifyContent: 'flex-start',
                      borderColor: option.swatch,
                      bgcolor: active ? option.swatch : 'transparent',
                      color: active ? '#fff' : 'text.primary',
                      '&:hover': {
                        bgcolor: active ? option.swatch : `${option.swatch}12`,
                        borderColor: option.swatch,
                      },
                    }}
                  >
                    <Box component="span" sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: option.swatch, border: active ? '1px solid rgba(255,255,255,0.9)' : '1px solid rgba(0,0,0,0.14)', mr: 1 }} />
                    {isHindi ? option.hindiLabel : text(option.label)}
                  </Button>
                );
              })}
            </Stack>
            <Alert severity="info" sx={{ mt: 1.5 }}>
              {text('This colour is saved for the app theme.')}
            </Alert>
          </SettingRow>

        <SettingRow label={text('PDF report colour')}>
          <FormControlLabel control={<Switch checked={pdfColorSameAsApp} onChange={event => setPdfReportColor(event.target.checked ? null : (pdfThemePalette || themePalette))} />} label={text('Use app colour')} />
          <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {THEME_PALETTE_OPTIONS.map(option => {
              const active = option.value === (pdfColorSameAsApp ? themePalette : (pdfThemePalette || themePalette));
              return <Button key={option.value} disabled={pdfColorSameAsApp} variant={active ? 'contained' : 'outlined'}
                aria-pressed={active} onClick={() => setPdfReportColor(option.value)}
                sx={{ minWidth: 106, borderColor: option.swatch, bgcolor: active ? option.swatch : 'transparent', color: active ? '#fff' : 'text.primary', '&:hover': { bgcolor: active ? option.swatch : `${option.swatch}12` } }}>
                <Box component="span" sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: option.swatch, border: '1px solid #ffffff80', mr: 1 }} />
                {language === 'hi' ? option.hindiLabel : translateInline(language, option.label)}
              </Button>;
            })}
          </Stack>
          <Alert severity="info" sx={{ mt: 1 }}>{text('Reports follow the app colour by default. Turn this off to choose a separate report colour.')}</Alert>
        </SettingRow>
        </SettingSection>

        <SettingSection title="Calculation defaults">
          <SettingRow label="Chart style">
            <ToggleButtonGroup exclusive value={chartStyle} onChange={changeChartStyle} fullWidth>
              <ToggleButton value="north">North</ToggleButton>
              <ToggleButton value="south">South</ToggleButton>
              <ToggleButton value="east">East</ToggleButton>
            </ToggleButtonGroup>
          </SettingRow>

          <SettingRow label={text('Default section after chart creation')}>
            <Select
              fullWidth
              size="small"
              value={defaultChartSection}
              onChange={(event) => setDefaultChartSection(event.target.value as DefaultChartSection)}
            >
              {DEFAULT_CHART_SECTION_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {text(option.label)}
                </MenuItem>
              ))}
            </Select>
          </SettingRow>

          <SettingRow label={isHindi ? 'हाउस सिस्टम' : 'House system'}>
            <Select
              fullWidth
              size="small"
              value={houseSystem}
              onChange={(event) => setHouseSystem(event.target.value as HouseSystemMode)}
            >
              {HOUSE_SYSTEM_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {isHindi ? option.hindiLabel : option.label}
                </MenuItem>
              ))}
            </Select>
          </SettingRow>

          <SettingRow label={isHindi ? 'राहु/केतु गणना' : 'Rahu/Ketu calculation'}>
            <ToggleButtonGroup exclusive value={nodeMode} onChange={changeNodeMode} fullWidth>
              <ToggleButton value="true">{isHindi ? 'ट्रू नोड' : 'True Node'}</ToggleButton>
              <ToggleButton value="mean">{isHindi ? 'मीन नोड' : 'Mean Node'}</ToggleButton>
            </ToggleButtonGroup>
          </SettingRow>

          <SettingRow label={isHindi ? 'अयनांश' : 'Ayanamsha'}><AyanamshaSettings /></SettingRow>

          <SettingRow label={isHindi ? 'बाहरी ग्रह' : 'Outer planets'}>
            <FormControlLabel
              control={<Switch checked={showOuterPlanets} onChange={(event) => setShowOuterPlanets(event.target.checked)} />}
              label={isHindi ? 'Uranus, Neptune और Pluto दिखाएं' : 'Show Uranus, Neptune and Pluto'}
            />
          </SettingRow>

          <SettingRow label={text('Horary charts')}>
            <FormControlLabel
              control={<Switch checked={saveHoraryCharts} onChange={(event) => setSaveHoraryCharts(event.target.checked)} />}
              label={text('Save Horary charts')}
            />
            <Alert severity="info" sx={{ mt: 1 }}>
              {text('Off by default. When enabled, new Horary charts are saved to your account; otherwise they remain temporary.')}
            </Alert>
          </SettingRow>

          <SettingRow label={text('PDF Tajik Default Year')}>
            <Select fullWidth size="small" value={pdfTajikDefaultYear} onChange={(event) => setPdfTajikDefaultYear(Number(event.target.value))}>
              {Array.from({ length: 5 }, (_, index) => new Date().getFullYear() - 2 + index).map((year) => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
            <Alert severity="info" sx={{ mt: 1 }}>
              {text('Only the selected Tajik year will be included in the PDF report.')}
            </Alert>
          </SettingRow>

          <SettingRow label={isHindi ? 'डिफॉल्ट शहर / टाइमज़ोन' : 'Default city / timezone'}>
            <CitySearch
              value={defaultCity as any}
              onChange={(city) => setDefaultCity(city as DefaultCity | null)}
              label={isHindi ? 'डिफॉल्ट शहर' : 'Default city'}
              placeholder={isHindi ? 'अपना डिफॉल्ट शहर खोजें' : 'Search your default city'}
            />
            <Alert severity={defaultCity ? 'success' : 'info'} sx={{ mt: 1.5 }}>
              {defaultCity
                ? `${defaultCity.displayName || defaultCity.name} · ${defaultCity.timezone?.name || 'Timezone'}`
                : 'No default city selected. Calculation pages will use New Delhi when a fallback is needed.'}
            </Alert>
            {defaultCity && (
              <Button sx={{ mt: 1 }} color="warning" onClick={() => setDefaultCity(null)}>
                {isHindi ? 'Default city हटाएं' : 'Clear default city'}
              </Button>
            )}
          </SettingRow>
        </SettingSection>

        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2.5, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="h2" sx={{ fontSize: 16 }}>{text('Export all charts', 'सभी चार्ट एक्सपोर्ट करें')}</Typography>
          <IconButton aria-label={text('Export all charts', 'सभी चार्ट एक्सपोर्ट करें')} onClick={exportCharts} disabled={exportingCharts}><FileDownloadOutlined /></IconButton>
        </Box>
        {exportError && <Alert severity="error" sx={{ mb: 2 }}>{exportError}</Alert>}
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2.5, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="h2" sx={{ fontSize: 16 }}>{text('Import Kundlistar chart file', 'Kundlistar चार्ट फ़ाइल इम्पोर्ट करें')}</Typography>
          <Button component={Link} to="/import-kundlistar-chart-file" variant="outlined">{text('Import', 'इम्पोर्ट करें')}</Button>
        </Box>

        <Button fullWidth variant="outlined" onClick={resetDefaults}>
          Reset to defaults
        </Button>
      </Paper>
    </Container>
  );
};

function SettingSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2.5, mb: 3 }}>
      <Typography variant="h2" sx={{ fontSize: 16, mb: 2 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function SettingRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600 }}>
        {label}
      </Typography>
      <Box sx={{ mt: 1 }}>{children}</Box>
    </Box>
  );
}

export default Settings;
